import React, { type FC, useEffect, type JSX } from 'react';
import { useTranslation } from '@/app/components/translations/Translation';
import { Icon } from '@/app/common/icons/Icon';
import { ArrowContainer } from './styled';

type ArrowProps = {
  itemsLength: number;
  selectedItem: number;
  galleryContainerNode: HTMLDivElement | HTMLAnchorElement | Element | null;
  className: string;
  isOverlay: boolean;
  handleOnClick: (val: string) => void;
  useTransparency: boolean;
  isAlternativeDates?: boolean;
};

const handleScroll = ({
  className,
  selectedItem,
  galleryContainerNode,
  isOverlay,
  itemsLength,
  isAlternativeDates,
}: {
  className: string;
  selectedItem: number;
  galleryContainerNode: HTMLDivElement | HTMLAnchorElement | Element | null;
  isOverlay: boolean;
  itemsLength: number;
  isAlternativeDates?: boolean;
}) => {
  let selItem = selectedItem;
  if (className === 'arrow-left') {
    selItem = isAlternativeDates ? 0 : selectedItem - 1;
  }
  if (className === 'arrow-right') {
    selItem = isAlternativeDates ? itemsLength - 1 : selectedItem + 1;
  }
  const child = galleryContainerNode?.childNodes[selItem] as HTMLDivElement;

  if (child) {
    child.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: isOverlay ? 'nearest' : 'center',
    });
  }
};
const Arrow: FC<ArrowProps> = ({
  className,
  selectedItem,
  itemsLength,
  galleryContainerNode,
  isOverlay,
  handleOnClick,
  useTransparency,
  isAlternativeDates,
}): JSX.Element => {
  const { t } = useTranslation();

  let hideArrow = false;
  if (className === 'arrow-left') hideArrow = selectedItem === 0;
  if (className === 'arrow-right') hideArrow = selectedItem === itemsLength - 1;

  const ariaLabel =
    className === 'arrow-left'
      ? t('vacation.oss.cards.gallery.previous_image')
      : t('vacation.oss.cards.gallery.next_image');

  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    handleScroll({ className, selectedItem, galleryContainerNode, isOverlay, itemsLength, isAlternativeDates });

    handleOnClick(className);
  };

  useEffect(() => {
    if (selectedItem !== 0) {
      handleScroll({
        className,
        selectedItem: selectedItem - 1,
        galleryContainerNode,
        isOverlay,
        itemsLength,
        isAlternativeDates,
      });
    }
  }, []);

  return (
    <ArrowContainer
      type="button"
      className={className}
      onClick={handleArrowClick}
      hideArrow={hideArrow}
      isOverlay={isOverlay}
      useTransparency={useTransparency}
      aria-label={ariaLabel}
      disabled={hideArrow}
      tabIndex={hideArrow ? -1 : 0}
    >
      <Icon category="Arrows" name="icon-chevron-right" size={18} footprint={[7, 14]} color="iconPrimary" alt="" />
    </ArrowContainer>
  );
};

export default Arrow;
